import * as React from 'react'

import { Box, Chevron, Icon, Popover, useToggle } from '@factorialco/deprecated-design-system'
import { BreakConfigurationFragment, TimesheetPageShiftFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import TooltipHeader from 'modules/attendance/components/ClockIn/Tooltip/Header'
import TooltipRow from 'modules/attendance/components/ClockIn/Tooltip/Row'
import MinutesToText from 'modules/attendance/components/MinutesToText'
import { shouldGreyOut } from 'modules/attendance/lib/dayRowStyle'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import type CalendarDay from 'modules/attendance/store/models/CalendarDay'

export interface Props {
  breaksMinutesRounded: number
  calendarDay: CalendarDay
  allBreaks: BreakConfigurationFragment[]
  shifts: TimesheetPageShiftFragment[]
}

interface BreakBreakdown {
  name?: string
  minutes?: number
}

/* Example:
 --Assume the employee took 4 breaks
 Lunch break: 30 minutes
 Coffee break: 15 minutes
 Lunch break: 15 minutes
 Coffee break: 60 minutes
 --This method group the breaks by the type and sum the minutes for each type
 will return
 Lunch break: 45 minutes
 Coffee break: 75 minutes
*/
const createBreaksBreakdown = (
  breakShifts: TimesheetPageShiftFragment[],
  allBreaks: BreakConfigurationFragment[]
): BreakBreakdown[] => {
  return allBreaks.map((breakConfig) => {
    const breakTypeMinutes = breakShifts
      .filter((shift) => shift.timeSettingsBreakConfiguration?.id === breakConfig.id)
      .reduce((acc, shift) => acc + shift.minutes, 0)

    return {
      name: breakConfig.name,
      minutes: breakTypeMinutes,
    }
  })
}

export const BreaksColumn = ({ breaksMinutesRounded, calendarDay, allBreaks, shifts }: Props) => {
  const [open, toggle] = useToggle(false)

  const { is_laborable, is_leave } = calendarDay.attributes
  const isGrayedOut = shouldGreyOut({
    worked: breaksMinutesRounded,
    is_leave,
    is_laborable,
  })
  const grayedOutColor = isGrayedOut ? 'grey700' : 'grey800'

  const breakShifts = AttendanceShiftUtils.breakShits(shifts)
  const trackedBreakMinutes = AttendanceShiftUtils.trackedBreaksdMinutes(breakShifts)
  const totalBreakMinutes = trackedBreakMinutes + breaksMinutesRounded // corrective breaks
  const breaksBreakdown = createBreaksBreakdown(breakShifts, allBreaks)

  return (
    <Popover.ItemList attachment="bottom center" toggle={toggle} open={open} gapDivider={false}>
      <Box inline flexDirection="row" gap="s4" alignItems="center">
        <Box paddingRight="s4">
          <Icon.Medium icon="coffee" color={grayedOutColor} />
        </Box>
        <MinutesToText
          value={totalBreakMinutes}
          weight="medium"
          color={grayedOutColor}
          alwaysRemainder
        />
        <Chevron direction={open ? 'up' : 'down'} type="terciary" />
      </Box>

      <TooltipHeader title={i18n.t('clock_in.breaks.break_time')} minutes={totalBreakMinutes} />

      {breaksBreakdown.map((breakItem) =>
        breakItem && breakItem?.name && breakItem.minutes && breakItem.minutes > 0 ? (
          <TooltipRow
            key={breakItem.name}
            label={breakItem.name}
            value={breakItem.minutes ?? 0}
            multiLine
          />
        ) : null
      )}

      {breaksMinutesRounded > 0 && (
        <TooltipRow
          label={i18n.t('clock_in.breaks.corrective_breaks.title')}
          value={breaksMinutesRounded}
          multiLine
        />
      )}
    </Popover.ItemList>
  )
}
